import { defineStore } from 'pinia'
import { injToken } from '@shared/data/token'
import { isCosmosWallet } from '@injectivelabs/wallet-base/light'
import { CHAIN_ID, ETHEREUM_CHAIN_ID } from '@shared/utils/constant'
import { getIndexerAccountPortfolioApi } from '@shared/service/account'
import { DEFAULT_MIN_GAS } from '@/app/utils/constants'
import { getIndexerGrpcArchiverApi } from '@/app/Services'
import { fetchAccountDetails } from '@/app/services/account'
import {
  createRequestKey,
  CLIENT_REQUEST_KEYS,
  apiCallsRequestCache
} from '@/app/services/apiCallsRequestCache'
import {
  getUsdcBankBalance,
  getUsdcSubaccountBalances,
  getUsdcSubaccountBalancesMap,
  getDefaultAccountUsdcBalances
} from '@/app/client/utils/account'
import { Duration, BusEvents } from '@/types/enums'
import type { AccountStats } from '@injectivelabs/sdk-ts'
import type { Wallet } from '@injectivelabs/wallet-base/light'
import type { SubaccountBalance } from '@/types/balance'

const importAccountStream = () => import('./stream')
const importAccountMessage = () => import('./message')

type AccountStoreState = {
  pubKey?: string
  subaccountId: string
  usdcBankBalance: string
  singerInjBalance: string
  accountStats: AccountStats | Record<string, number>
  historicalPnl: {
    time: number
    value: number
  }[]
  historicalBalance: {
    time: number
    value: number
  }[]
  usdcSubaccountBalancesMap: Record<string, SubaccountBalance[]>
}

const initialStateFactory = (): AccountStoreState => ({
  pubKey: '',
  subaccountId: '',
  accountStats: {},
  historicalPnl: [],
  usdcBankBalance: '0',
  singerInjBalance: '0',
  historicalBalance: [],
  usdcSubaccountBalancesMap: {}
})

export const useAccountStore = defineStore('account', {
  state: (): AccountStoreState => initialStateFactory(),
  getters: {
    hasSignerGasBalance: (state: AccountStoreState) => {
      return toBigNumber(state.singerInjBalance).gt(DEFAULT_MIN_GAS)
    },

    defaultUsdcSubaccountBalances: (state: AccountStoreState) => {
      const sharedWalletStore = useSharedWalletStore()

      if (!sharedWalletStore.authZOrDefaultSubaccountId) {
        return []
      }

      return state.usdcSubaccountBalancesMap[
        sharedWalletStore.authZOrDefaultSubaccountId
      ]
    }
  },
  actions: {
    setPubKey(pubKey?: string) {
      this.pubKey = pubKey
    },

    setUsdcBankBalance(usdcBankBalance: string) {
      this.usdcBankBalance = usdcBankBalance
    },

    setSubaccountId(subaccountId: string) {
      this.subaccountId = subaccountId
    },

    setSingerInjBalance(singerInjBalance: string) {
      this.singerInjBalance = singerInjBalance
    },

    setAccountStats(accountStats: AccountStoreState['accountStats']) {
      this.accountStats = accountStats
    },

    setHistoricalPnl(historicalPnl: AccountStoreState['historicalPnl']) {
      this.historicalPnl = historicalPnl
    },

    setHistoricalBalance(
      historicalBalance: AccountStoreState['historicalBalance']
    ) {
      this.historicalBalance = historicalBalance
    },

    setUsdcSubaccountBalancesMap(
      usdcSubaccountBalancesMap: AccountStoreState['usdcSubaccountBalancesMap']
    ) {
      this.usdcSubaccountBalancesMap = getUsdcSubaccountBalancesMap(
        usdcSubaccountBalancesMap
      )
    },

    setUsdcSubaccountBalances({
      subaccountId,
      usdcSubaccountBalances
    }: {
      subaccountId: string
      usdcSubaccountBalances: SubaccountBalance[]
    }) {
      const accountStore = useAccountStore()

      accountStore.setUsdcSubaccountBalancesMap({
        ...accountStore.usdcSubaccountBalancesMap,
        [subaccountId]: getUsdcSubaccountBalances(usdcSubaccountBalances)
      })
    },

    cancelBankBalanceStream: lazyPiniaAction(
      importAccountStream,
      'cancelBankBalanceStream'
    ),
    cancelSubaccountBalanceStream: lazyPiniaAction(
      importAccountStream,
      'cancelSubaccountBalanceStream'
    ),
    deposit: lazyPiniaAction(importAccountMessage, 'deposit'),
    withdraw: lazyPiniaAction(importAccountMessage, 'withdraw'),
    withdrawToSafe: lazyPiniaAction(importAccountMessage, 'withdrawToSafe'),
    withdrawToMain: lazyPiniaAction(importAccountMessage, 'withdrawToMain'),
    externalTransfer: lazyPiniaAction(importAccountMessage, 'externalTransfer'),
    streamBankBalance: lazyPiniaAction(
      importAccountStream,
      'streamBankBalance'
    ),
    streamSubaccountBalance: lazyPiniaAction(
      importAccountStream,
      'streamSubaccountBalance'
    ),

    updateSubaccount(subaccountId: string) {
      const accountStore = useAccountStore()

      accountStore.setSubaccountId(subaccountId)

      useEventBus(BusEvents.SubaccountChange).emit(subaccountId)
    },

    async fetchSignerInjBalance() {
      const accountStore = useAccountStore()
      const sharedWalletStore = useSharedWalletStore()

      if (!sharedWalletStore.isUserConnected) {
        return
      }

      const indexerAccountPortfolioApi = await getIndexerAccountPortfolioApi()
      const accountPortfolio = await apiCallsRequestCache.dedupe(
        createRequestKey(CLIENT_REQUEST_KEYS.accountPortfolioBalances, {
          address: sharedWalletStore.injectiveAddress
        }),
        () =>
          indexerAccountPortfolioApi.fetchAccountPortfolioBalances(
            sharedWalletStore.injectiveAddress
          )
      )

      const injBalance =
        accountPortfolio.bankBalancesList.find(
          ({ denom }) => denom === injToken.denom
        )?.amount || '0'

      accountStore.setSingerInjBalance(injBalance)
    },

    async fetchAccountPortfolioBalances() {
      const accountStore = useAccountStore()
      const sharedWalletStore = useSharedWalletStore()

      if (!sharedWalletStore.isUserConnected) {
        return
      }

      const indexerAccountPortfolioApi = await getIndexerAccountPortfolioApi()
      const accountPortfolio = await apiCallsRequestCache.dedupe(
        createRequestKey(CLIENT_REQUEST_KEYS.accountPortfolioBalances, {
          address: sharedWalletStore.authZOrInjectiveAddress
        }),
        () =>
          indexerAccountPortfolioApi.fetchAccountPortfolioBalances(
            sharedWalletStore.authZOrInjectiveAddress
          )
      )

      const defaultAccountBalances = getDefaultAccountUsdcBalances(
        accountPortfolio.subaccountsList,
        sharedWalletStore.authZOrDefaultSubaccountId
      )
      const usdcBankBalance = getUsdcBankBalance(
        accountPortfolio.bankBalancesList || []
      )

      accountStore.setUsdcBankBalance(usdcBankBalance)
      accountStore.setUsdcSubaccountBalancesMap({
        [sharedWalletStore.authZOrDefaultSubaccountId]: defaultAccountBalances
      })
    },

    async fetchAddressFromWalletStrategy(wallet: Wallet) {
      try {
        const [{ WalletStrategy }, { alchemyRpcEndpoint }] = await Promise.all([
          import('@injectivelabs/wallet-strategy'),
          import('@shared/wallet')
        ])
        const walletStrategy = new WalletStrategy({
          wallet,
          chainId: CHAIN_ID,
          evmOptions: {
            evmChainId: ETHEREUM_CHAIN_ID,
            rpcUrl: alchemyRpcEndpoint
          },
          strategies: {}
        })

        const addresses = await walletStrategy.enableAndGetAddresses()
        const [address] = addresses

        if (!address) {
          return
        }

        // cosmos returns inj address
        if (isCosmosWallet(wallet)) {
          return address
        }

        // eth returns eth address so convert to inj address
        const { getInjectiveAddress } = await import(
          '@injectivelabs/sdk-ts/utils/address'
        )

        return getInjectiveAddress(address)
      } catch {
        // silently fail
      }
    },

    async fetchPubKey() {
      const accountStore = useAccountStore()
      const sharedWalletStore = useSharedWalletStore()

      const address = sharedWalletStore.injectiveAddress

      if (!address) {
        accountStore.setPubKey('')

        return
      }

      try {
        if (sharedWalletStore.isSSOAuth) {
          const accountDetails = await fetchAccountDetails(address)
          const publicKeyBase64 = accountDetails.pubKey.key

          accountStore.setPubKey(publicKeyBase64)

          return
        }

        const { getWalletStrategy } = await import('@shared/wallet')
        const walletStrategy = await getWalletStrategy()

        accountStore.setPubKey(await walletStrategy.getPubKey(address))
      } catch {
        // silently fail
      }
    },

    async fetchAccountStats(period: Duration = Duration.OneMonth) {
      const indexerGrpcArchiverApi = await getIndexerGrpcArchiverApi()

      const accountStore = useAccountStore()
      const sharedWalletStore = useSharedWalletStore()

      if (!sharedWalletStore.authZOrInjectiveAddress) {
        return
      }

      const response = await apiCallsRequestCache.cached({
        key: createRequestKey(CLIENT_REQUEST_KEYS.portfolioOverview, {
          period,
          type: CLIENT_REQUEST_KEYS.accountStats,
          account: sharedWalletStore.authZOrInjectiveAddress
        }),
        request: () =>
          indexerGrpcArchiverApi.fetchAccountStats({
            period,
            account: sharedWalletStore.authZOrInjectiveAddress
          })
      })

      accountStore.setAccountStats(response)
    },

    async fetchHistoricalBalance(resolution: Duration = Duration.OneMonth) {
      const indexerGrpcArchiverApi = await getIndexerGrpcArchiverApi()

      const accountStore = useAccountStore()
      const sharedWalletStore = useSharedWalletStore()

      if (!sharedWalletStore.authZOrInjectiveAddress) {
        return
      }

      const { t, v } = await apiCallsRequestCache.cached({
        key: createRequestKey(CLIENT_REQUEST_KEYS.portfolioOverview, {
          resolution,
          type: CLIENT_REQUEST_KEYS.historicalBalance,
          account: sharedWalletStore.authZOrInjectiveAddress
        }),
        request: () =>
          indexerGrpcArchiverApi.fetchHistoricalBalance({
            resolution,
            account: sharedWalletStore.authZOrInjectiveAddress
          })
      })

      const historicalBalance = t
        .map((time, index) => {
          return {
            time: time * 1000,
            value: v[index]
          }
        })
        .filter(
          (item): item is { time: number; value: number } =>
            item.value !== undefined
        )

      accountStore.setHistoricalBalance(historicalBalance.reverse())
    },

    async fetchHistoricalPnl(resolution: Duration = Duration.OneMonth) {
      const indexerGrpcArchiverApi = await getIndexerGrpcArchiverApi()

      const accountStore = useAccountStore()
      const sharedWalletStore = useSharedWalletStore()

      if (!sharedWalletStore.authZOrInjectiveAddress) {
        return
      }

      const { t, v } = await apiCallsRequestCache.cached({
        key: createRequestKey(CLIENT_REQUEST_KEYS.portfolioOverview, {
          resolution,
          type: CLIENT_REQUEST_KEYS.historicalPnl,
          account: sharedWalletStore.authZOrInjectiveAddress
        }),
        request: () =>
          indexerGrpcArchiverApi.fetchHistoricalRpnl({
            resolution,
            account: sharedWalletStore.authZOrInjectiveAddress
          })
      })

      const historicalPnl = t
        .map((time, index) => {
          return {
            time: time * 1000,
            value: v[index]
          }
        })
        .filter(
          (item): item is { time: number; value: number } =>
            item.value !== undefined
        )

      accountStore.setHistoricalPnl(historicalPnl.reverse())
    },

    async reset() {
      const accountStore = useAccountStore()

      await Promise.all([
        accountStore.cancelBankBalanceStream(),
        accountStore.cancelSubaccountBalanceStream()
      ])

      accountStore.$reset()
    }
  }
})
